import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Star, Clock, Globe, X, Check, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import type { FeaturedPurchase } from '../../types/types';

// Which purchase pays for a destaque decides two things the agent can't see
// otherwise: how many portals the property reaches (Pro = all of them) and how
// long it stays destacada (featured_until = the purchase's expiry). This dialog
// puts both on screen so the choice is informed. It serves two flows:
//   · activating — shown only when more than one purchase has a free slot;
//   · cambiar    — moving an already-destacada property between purchases.
interface Props {
  theme: 'light' | 'dark' | 'high-contrast';
  title: string;
  subtitle?: string;
  purchases: FeaturedPurchase[];
  /** Marks the purchase the property is already on (cambiar flow). */
  currentPurchaseId?: string | null;
  busy?: boolean;
  onPick: (purchaseId: string) => void;
  onClose: () => void;
}

function daysUntil(dateIso: string): number {
  const ms = new Date(dateIso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function formatDate(dateIso: string): string {
  return new Date(dateIso).toLocaleDateString('es-GT', { day: 'numeric', month: 'short', year: 'numeric' });
}

// tier lives on the package; category is the reliable signal on the summary row.
const isPro = (p: FeaturedPurchase) => p.package_category === 'featured_pro';

export const FeaturedPurchaseChooser: React.FC<Props> = ({
  theme, title, subtitle, purchases, currentPurchaseId, busy, onPick, onClose,
}) => {
  const isLight = theme !== 'dark';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Portalled to body — the page-entry transform breaks position:fixed.
  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
    >
      <div
        onClick={e => e.stopPropagation()}
        className={clsx(
          'w-full max-w-lg max-h-[85vh] overflow-auto rounded-3xl border p-6 space-y-4',
          isLight ? 'bg-white border-slate-200 shadow-2xl' : 'glass-panel border-white/10'
        )}
      >
        <div className="flex items-start gap-3">
          <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', isLight ? 'bg-amber-100 text-amber-600' : 'bg-amber-500/20 text-amber-400')}>
            <Star size={20} fill="currentColor" />
          </div>
          <div className="min-w-0">
            <h3 className={clsx('text-base font-bold', isLight ? 'text-slate-900' : 'text-white')}>{title}</h3>
            {subtitle && <p className={clsx('text-sm', isLight ? 'text-slate-500' : 'text-slate-400')}>{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className={clsx('ml-auto p-2 rounded-xl transition-colors shrink-0', isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-slate-400 hover:bg-white/10')}
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-2">
          {purchases.map(pur => {
            const current = pur.id === currentPurchaseId;
            const pro = isPro(pur);
            const full = pur.available_slots <= 0 && !current;
            return (
              <button
                key={pur.id}
                onClick={() => !full && !busy && onPick(pur.id)}
                disabled={full || busy}
                className={clsx(
                  'w-full text-left rounded-2xl border p-4 flex items-center gap-3 transition-all',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  current
                    ? isLight ? 'border-amber-400 bg-amber-50' : 'border-amber-500/50 bg-amber-500/10'
                    : isLight ? 'border-slate-200 bg-white hover:border-amber-300 hover:bg-amber-50/50' : 'border-white/10 bg-white/5 hover:border-amber-500/40 hover:bg-amber-500/5'
                )}
              >
                <div className="text-2xl shrink-0">{pur.package_icon || (pro ? '🌟' : '⭐')}</div>
                <div className="flex-1 min-w-0">
                  <p className={clsx('text-sm font-bold flex items-center gap-2', isLight ? 'text-slate-900' : 'text-white')}>
                    {pur.package_title}
                    {current && (
                      <span className={clsx('px-2 py-0.5 rounded-chip text-[9px] font-black uppercase tracking-widest', isLight ? 'bg-amber-200 text-amber-800' : 'bg-amber-500/25 text-amber-300')}>
                        Actual
                      </span>
                    )}
                  </p>
                  <p className={clsx('text-xs flex items-center gap-1.5 mt-0.5', isLight ? 'text-slate-500' : 'text-slate-400')}>
                    <Globe size={11} />
                    {pro ? 'Aparece en todos los portales' : 'Aparece en mapainmueble'}
                  </p>
                  <p className={clsx('text-xs flex items-center gap-1.5 mt-0.5', isLight ? 'text-slate-500' : 'text-slate-400')}>
                    <Clock size={11} />
                    Vence {formatDate(pur.expire_date)} · {daysUntil(pur.expire_date)} días
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className={clsx('text-sm font-bold', isLight ? 'text-slate-900' : 'text-white')}>
                    {pur.available_slots}
                  </p>
                  <p className={clsx('text-[9px] uppercase tracking-wider font-bold', pur.available_slots > 0 ? 'text-emerald-500' : 'text-slate-400')}>
                    {full ? 'lleno' : pur.available_slots === 1 ? 'libre' : 'libres'}
                  </p>
                </div>
                {busy ? <Loader2 size={16} className="animate-spin text-amber-500 shrink-0" /> : current ? <Check size={16} className="text-amber-500 shrink-0" /> : null}
              </button>
            );
          })}
        </div>

        <p className={clsx('text-xs', isLight ? 'text-slate-400' : 'text-slate-500')}>
          El destaque vence junto con el paquete que elijas.
        </p>
      </div>
    </div>,
    document.body
  );
};
